import {
  House, ChalkboardTeacher, Student, ChartBar, MagnifyingGlass,
  BookBookmark, Flask, PencilSimpleLine, Files, ChatCircleText,
  Microphone, Stack, BookOpen, Plus, SignOut, GearSix,
  IdentificationCard, Books,
  type Icon as PhosphorIconType,
  type IconWeight,
} from '@phosphor-icons/react';

/* Navigation icons used in the header rail + side menu.
   Legacy SVG paths removed — Phosphor is the single source. */

export type IconName =
  | 'home' | 'teacher' | 'student' | 'admin' | 'lab' | 'prompts' | 'documents'
  | 'chat' | 'voice' | 'parcours' | 'cours' | 'plus' | 'logout'
  | 'search' | 'curriculum' | 'settings' | 'classes' | 'resources';

const ICON_MAP: Record<IconName, PhosphorIconType> = {
  home:       House,
  teacher:    ChalkboardTeacher,
  student:    Student,
  admin:      ChartBar,
  search:     MagnifyingGlass,
  curriculum: BookBookmark,
  lab:        Flask,
  prompts:    PencilSimpleLine,
  documents:  Files,
  chat:       ChatCircleText,
  voice:      Microphone,
  parcours:   Stack,
  cours:      BookOpen,
  plus:       Plus,
  logout:     SignOut,
  settings:   GearSix,
  classes:    IdentificationCard,
  resources:  Books,
};

export function Icon({ name, size = 20, weight = 'regular' }: { name: IconName; size?: number; weight?: IconWeight }) {
  const PhosphorIcon = ICON_MAP[name];
  return <PhosphorIcon size={size} weight={weight} />;
}

/* `onDark` n est pas un gout : le bandeau lateral porte la meme couleur profonde
   dans les deux themes, donc la bascule `dark:` de Tailwind ne le decrit pas. Sans
   ce drapeau, le logo encre reste pose sur le bleu en theme clair et disparait. */
export function NaiaLogo({ height = 18, onDark = false }: { height?: number; onDark?: boolean }) {
  const style = { width: 'auto', maxHeight: height, objectFit: 'contain' } as const;
  if (onDark) {
    return <img src="/naia-logo-white.png" height={height} alt="Naïa-Edu" style={style} className="block" />;
  }
  return (
    <>
      <img src="/naia-logo-navy.png" height={height} alt="Naïa-Edu" style={style} className="block dark:hidden" />
      <img src="/naia-logo-white.png" height={height} alt="" aria-hidden style={style} className="hidden dark:block" />
    </>
  );
}

export function NaiaMark({ size = 30 }: { size?: number }) {
  return (
    <svg width={size} height={size * (24 / 32)} viewBox="0 0 32 24" aria-hidden focusable="false" className="block shrink-0">
      <path d="M8 13 L24 13 L24 18.5 C24 21 20.4 22.5 16 22.5 C11.6 22.5 8 21 8 18.5 Z" fill="var(--naia-blue)" opacity="0.55" />
      <path d="M16 4 L30 11 L16 18 L2 11 Z" fill="var(--naia-blue)" strokeLinejoin="round" />
      <path d="M30 11 V19.5" fill="none" stroke="var(--naia-green)" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="30" cy="20.6" r="1.8" fill="var(--naia-green)" />
    </svg>
  );
}

export function Bar() {
  return <span className="block w-[22px] h-[2.5px] rounded-sm bg-[var(--naia-navy)]" />;
}

export function Divider() {
  return <div className="naia-rail__divider h-px bg-[var(--rail-line)] my-1.5 mx-1.5" />;
}
